import { useMemo, useCallback, useState, lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { MortgageSlider } from '@/components/calculator/MortgageSlider'
import { ResultsSummary } from '@/components/calculator/ResultsSummary'
import { AffordabilityGauge } from '@/components/calculator/AffordabilityGauge'
import { OverpaymentSummary } from '@/components/calculator/OverpaymentSummary'
import type { ExtraCosts } from '@/components/calculator/ExtraCostsSummary'

const RentVsBuy = lazy(() => import('@/components/calculator/RentVsBuy').then(m => ({ default: m.RentVsBuy })))
const ExtraCostsSummary = lazy(() => import('@/components/calculator/ExtraCostsSummary').then(m => ({ default: m.ExtraCostsSummary })))
import type { Scenario } from '@/components/calculator/ScenarioComparison'

// Lazy load below-the-fold components (recharts is ~200KB)
const PaymentChart = lazy(() => import('@/components/calculator/PaymentChart').then(m => ({ default: m.PaymentChart })))
const ScenarioComparison = lazy(() => import('@/components/calculator/ScenarioComparison').then(m => ({ default: m.ScenarioComparison })))
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { calculateMortgage, generateOverpaymentComparison, calculateRentVsBuy } from '@/lib/mortgage'
import type { MortgageInput, InstallmentType } from '@/lib/mortgage'
import { DEFAULTS, LIMITS, formatPLN, formatPercent, estimateNotaryFee } from '@/lib/constants'
import { usePersistedState } from '@/lib/usePersistedState'
import { useShareUrl } from '@/lib/useShareUrl'
import { SEOHead } from '@/components/layout/SEOHead'
import { EmbedPopup } from '@/components/layout/EmbedPopup'
import { HomepageContent } from '@/components/calculator/HomepageContent'
import { Button } from '@/components/ui/button'
import { graph, webApplication, organization, faqPage, type FaqItem } from '@/lib/jsonLd'

const RocketIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
  </svg>
)

const HouseIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
  </svg>
)

const PercentIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const CalendarIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
  </svg>
)

const BankIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" />
  </svg>
)

const WalletIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3" />
  </svg>
)

const ChartIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6L9 12.75l4.286-4.286a11.948 11.948 0 014.306 6.43l.776 2.898m0 0l3.182-5.511m-3.182 5.51l-5.511-3.181" />
  </svg>
)

const KeyIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
  </svg>
)

const ReceiptIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
)

function SectionLabel({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-1">
      <span className="text-primary">{icon}</span>
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {children}
      </span>
    </div>
  )
}

/**
 * Homepage FAQ — questions Polish users actually ask before trusting a mortgage
 * calculator. Each answer leads with the answer, stays factual, and matches what
 * the calculator actually does. Embedded as JSON-LD FAQPage so AI search engines
 * extract Q/A pairs verbatim into answers.
 */
const HOMEPAGE_FAQS: FaqItem[] = [
  {
    question: 'Czy kalkulator kredytu hipotecznego RatyGo jest bezpłatny?',
    answer:
      'Tak, RatyGo jest w pełni bezpłatny i dostępny bez rejestracji. Wszystkie funkcje — porównanie rat, symulacja nadpłat, kalkulacja zdolności kredytowej i porównanie scenariuszy — są darmowe.',
  },
  {
    question: 'Czym różnią się raty równe od malejących?',
    answer:
      'Raty równe (annuitetowe) są stałe przez cały okres kredytowania, więc łatwiej planować budżet. Raty malejące zaczynają się wyższe i z czasem maleją — dzięki temu suma zapłaconych odsetek jest niższa o nawet kilkadziesiąt tysięcy złotych w skali całego kredytu.',
  },
  {
    question: 'Czy kalkulator uwzględnia WIBOR i marżę banku?',
    answer:
      'Tak. RatyGo pozwala osobno ustawić stawkę WIBOR (np. WIBOR 6M) oraz marżę banku. Suma obu wartości tworzy oprocentowanie kredytu. Po zmianie WIBOR rata przelicza się natychmiast.',
  },
  {
    question: 'Czy mogę symulować nadpłatę kredytu hipotecznego?',
    answer:
      'Tak. Wystarczy podać miesięczną kwotę nadpłaty — RatyGo pokaże, ile zaoszczędzisz na odsetkach i o ile skrócisz okres spłaty kredytu w porównaniu z wariantem bez nadpłat.',
  },
  {
    question: 'Czy kalkulator pokazuje całkowity koszt kredytu hipotecznego?',
    answer:
      'Tak. Po wpisaniu parametrów zobaczysz miesięczną ratę, sumę zapłaconych odsetek oraz całkowity koszt kredytu. Możesz też włączyć dodatkowe koszty: prowizję banku, ubezpieczenie nieruchomości, podatek PCC oraz opłatę notarialną.',
  },
  {
    question: 'Czy mogę porównać zakup mieszkania na kredyt z wynajmem?',
    answer:
      'Tak. RatyGo zawiera moduł "Kredyt vs wynajem" — wystarczy podać wysokość czynszu najmu, aby zobaczyć, kiedy zakup na kredyt staje się tańszy od wynajmu i ile zyskujesz lub tracisz w skali całego okresu.',
  },
  {
    question: 'Czy mogę zapisać i porównać kilka wariantów kredytu?',
    answer:
      'Tak. Możesz zapisać do trzech scenariuszy obok siebie i porównać raty, koszt całkowity oraz harmonogram spłaty w jednej tabeli. Wynik możesz też udostępnić przez link — wszystkie parametry zapisują się w adresie URL.',
  },
]

/**
 * Composed Schema.org graph for the homepage:
 * - WebApplication (calculator tool, featureList for AI summarization)
 * - Organization (publisher, referenced by @id from other pages)
 * - FAQPage (atomic Q/A pairs — highest-leverage AI citation surface)
 */
const HOMEPAGE_JSONLD = graph(webApplication(), organization(), faqPage(HOMEPAGE_FAQS))

export function CalculatorPage() {
  const { t } = useTranslation()

  const {
    propertyPrice, setPropertyPrice,
    downPaymentPercent, setDownPaymentPercent,
    loanYears, setLoanYears,
    wiborRate, setWiborRate,
    bankMargin, setBankMargin,
    monthlyIncome, setMonthlyIncome,
    monthlyOverpayment, setMonthlyOverpayment,
    monthlyRent, setMonthlyRent,
    bankCommissionPercent, setBankCommissionPercent,
    propertyInsuranceYearly, setPropertyInsuranceYearly,
    isSecondaryMarket, setIsSecondaryMarket,
    installmentType, setInstallmentType,
  } = usePersistedState({ ...DEFAULTS, installmentType: 'equal' })
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [rentEnabled, setRentEnabled] = useState(monthlyRent > 0)
  const [extraCostsEnabled, setExtraCostsEnabled] = useState(false)

  const annualRate = wiborRate + bankMargin

  const input: MortgageInput = useMemo(
    () => ({ propertyPrice, downPaymentPercent, loanYears, annualRate }),
    [propertyPrice, downPaymentPercent, loanYears, annualRate]
  )

  const result = useMemo(() => calculateMortgage(input, installmentType), [input, installmentType])

  const overpaymentComparison = useMemo(
    () => (monthlyOverpayment > 0 ? generateOverpaymentComparison(input, monthlyOverpayment, installmentType) : null),
    [input, monthlyOverpayment, installmentType]
  )

  const rentVsBuyResult = useMemo(
    () => (monthlyRent > 0 ? calculateRentVsBuy(input, result, monthlyRent) : null),
    [input, result, monthlyRent]
  )

  const extraCosts: ExtraCosts = useMemo(() => {
    const bankCommission = result.loanAmount * (bankCommissionPercent / 100)
    const pccTax = isSecondaryMarket ? propertyPrice * 0.02 : 0
    const notaryFee = Math.round(estimateNotaryFee(propertyPrice))
    const valuation = 500
    return {
      bankCommission,
      pccTax,
      notaryFee,
      valuation,
      insuranceYearly: propertyInsuranceYearly,
      totalOneTime: bankCommission + pccTax + notaryFee + valuation,
    }
  }, [result.loanAmount, bankCommissionPercent, isSecondaryMarket, propertyPrice, propertyInsuranceYearly])

  const handleSaveScenario = useCallback(() => {
    if (scenarios.length >= 3) return
    setScenarios((prev) => [...prev, { id: Date.now(), input, result }])
  }, [scenarios.length, input, result])

  const handleClearScenarios = useCallback(() => setScenarios([]), [])

  const { share, copied } = useShareUrl()
  const handleShare = useCallback(() => {
    share({ propertyPrice, downPaymentPercent, loanYears, wiborRate, bankMargin, monthlyOverpayment, monthlyRent, installmentType })
  }, [share, propertyPrice, downPaymentPercent, loanYears, wiborRate, bankMargin, monthlyOverpayment, monthlyRent, installmentType])

  const downPaymentAmount = propertyPrice * (downPaymentPercent / 100)

  return (
    <div className="relative">
      <SEOHead
        title="RatyGo — Kalkulator Kredytu Hipotecznego"
        description="Interaktywny kalkulator kredytu hipotecznego. Porównaj raty równe i malejące, symuluj nadpłaty, sprawdź zdolność kredytową."
        slug="/"
        keywords={['kalkulator kredytu hipotecznego', 'kredyt hipoteczny', 'raty równe', 'raty malejące', 'WIBOR']}
        jsonLd={HOMEPAGE_JSONLD}
      />
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-indigo-400/10 to-violet-400/10 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -left-40 w-80 h-80 bg-gradient-to-br from-teal-400/8 to-emerald-400/8 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-gradient-to-br from-violet-400/6 to-pink-400/6 rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_380px]">
          {/* Left column: Sliders */}
          <div className="flex flex-col gap-8">
            <motion.div
              className="space-y-6 rounded-2xl bg-white/80 backdrop-blur-sm p-6 shadow-sm border border-border"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* Installment Type Toggle + Share */}
              <div id="installment-type"></div>
              <div className="flex items-center gap-3">
                <Tabs
                  value={installmentType}
                  onValueChange={(v) => setInstallmentType(v as InstallmentType)}
                  className="flex-1"
                >
                  <TabsList className="w-full grid grid-cols-2">
                    <TabsTrigger value="equal">{t('installment.equal')}</TabsTrigger>
                    <TabsTrigger value="decreasing">{t('installment.decreasing')}</TabsTrigger>
                  </TabsList>
                </Tabs>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShare}
                  aria-label={copied ? 'Link copied' : 'Copy share link'}
                  className={`shrink-0 h-9 px-3 gap-1.5 transition-colors ${copied ? 'border-emerald-300 text-emerald-600' : ''}`}
                >
                  {copied ? (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      <span className="text-xs">OK</span>
                    </>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  )}
                </Button>
              </div>

              {/* Loan Details Section */}
              <SectionLabel icon={<HouseIcon />}>
                {t('calculator.propertyPrice')}
              </SectionLabel>
              <MortgageSlider
                label={t('calculator.propertyPrice')}
                value={propertyPrice}
                onChange={setPropertyPrice}
                formatValue={formatPLN}
                icon={<HouseIcon />}
                {...LIMITS.propertyPrice}
              />

              <MortgageSlider
                label={t('calculator.downPayment')}
                value={downPaymentPercent}
                onChange={setDownPaymentPercent}
                formatValue={(v) => `${v}%`}
                secondaryLabel={t('calculator.downPaymentAmount', {
                  amount: formatPLN(downPaymentAmount),
                })}
                icon={<PercentIcon />}
                {...LIMITS.downPaymentPercent}
              />

              <MortgageSlider
                label={t('calculator.loanPeriod')}
                value={loanYears}
                onChange={setLoanYears}
                formatValue={(v) => `${v} ${t('calculator.years')}`}
                icon={<CalendarIcon />}
                {...LIMITS.loanYears}
              />

              {/* Interest Rate Section */}
              <div id="interest-rate" className="border-t pt-6">
                <SectionLabel icon={<ChartIcon />}>
                  {t('calculator.interestRate')}
                </SectionLabel>
                <div className="space-y-6 mt-4 p-4 rounded-xl bg-gradient-to-br from-indigo-50/50 to-violet-50/30">
                  <MortgageSlider
                    label={`${t('calculator.wibor')} (WIBOR 6M)`}
                    value={wiborRate}
                    onChange={setWiborRate}
                    formatValue={formatPercent}
                    tooltip={t('calculator.wiborTooltip')}
                    icon={<ChartIcon />}
                    {...LIMITS.wiborRate}
                  />

                  <MortgageSlider
                    label={t('calculator.bankMargin')}
                    value={bankMargin}
                    onChange={setBankMargin}
                    formatValue={formatPercent}
                    tooltip={t('calculator.bankMarginTooltip')}
                    icon={<BankIcon />}
                    {...LIMITS.bankMargin}
                  />
                </div>
              </div>

              {/* Income Section */}
              <div id="affordability" className="border-t pt-6">
                <SectionLabel icon={<WalletIcon />}>
                  {t('calculator.monthlyIncome')}
                </SectionLabel>
                <div className="mt-4">
                  <MortgageSlider
                    label={t('calculator.monthlyIncome')}
                    value={monthlyIncome}
                    onChange={setMonthlyIncome}
                    formatValue={formatPLN}
                    tooltip={t('calculator.incomeTooltip')}
                    icon={<WalletIcon />}
                    {...LIMITS.monthlyIncome}
                  />
                </div>
              </div>

              {/* Overpayment Section */}
              <div id="overpayment" className="border-t pt-6">
                <SectionLabel icon={<RocketIcon />}>
                  {t('overpayment.title')}
                </SectionLabel>
                <div className="mt-4 p-4 rounded-xl bg-gradient-to-br from-emerald-50/50 to-teal-50/30">
                  <MortgageSlider
                    label={t('overpayment.monthlyOverpayment')}
                    value={monthlyOverpayment}
                    onChange={setMonthlyOverpayment}
                    formatValue={formatPLN}
                    tooltip={t('overpayment.tooltip')}
                    icon={<RocketIcon />}
                    {...LIMITS.monthlyOverpayment}
                  />
                </div>
                <OverpaymentSummary
                  comparison={overpaymentComparison ?? { baseTotalMonths: 0, baseTotalInterest: 0, overpaymentTotalMonths: 0, overpaymentTotalInterest: 0, monthsSaved: 0, interestSaved: 0, baseYearlySummary: [], overpaymentYearlySummary: [] }}
                  monthlyOverpayment={monthlyOverpayment}
                  baseMonthlyPayment={result.monthlyPayment}
                />
              </div>

              {/* Extra Costs Section */}
              <div id="extra-costs" className="border-t pt-6">
                <button
                  type="button"
                  onClick={() => setExtraCostsEnabled(prev => !prev)}
                  className="w-full rounded-2xl bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-200/50 p-5 flex items-center gap-4 cursor-pointer hover:from-amber-200/80 hover:to-orange-200/80 transition-colors group"
                >
                  <span className="text-2xl shrink-0">&#129534;</span>
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-sm font-bold text-amber-800">{t('extraCosts.title')}</p>
                    <p className="text-xs text-amber-600/70">{t('extraCosts.subtitle')}</p>
                  </div>
                  <div
                    className={`relative shrink-0 w-11 h-6 rounded-full transition-colors duration-200 ${extraCostsEnabled ? 'bg-amber-500' : 'bg-gray-300'}`}
                  >
                    <div
                      className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${extraCostsEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </div>
                </button>

                <AnimatePresence>
                  {extraCostsEnabled && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      {/* Market type toggle */}
                      <div className="mt-4 mb-3">
                        <Tabs
                          value={isSecondaryMarket ? 'secondary' : 'primary'}
                          onValueChange={(v) => setIsSecondaryMarket(v === 'secondary')}
                        >
                          <TabsList className="w-full grid grid-cols-2">
                            <TabsTrigger value="primary">{t('extraCosts.primaryMarket')}</TabsTrigger>
                            <TabsTrigger value="secondary">{t('extraCosts.secondaryMarket')}</TabsTrigger>
                          </TabsList>
                        </Tabs>
                      </div>

                      {/* Sliders */}
                      <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50/50 to-orange-50/30 space-y-6">
                        <MortgageSlider
                          label={t('extraCosts.bankCommission')}
                          value={bankCommissionPercent}
                          onChange={setBankCommissionPercent}
                          formatValue={formatPercent}
                          tooltip={t('extraCosts.bankCommissionTooltip')}
                          icon={<ReceiptIcon />}
                          {...LIMITS.bankCommissionPercent}
                        />
                        <MortgageSlider
                          label={t('extraCosts.insurance')}
                          value={propertyInsuranceYearly}
                          onChange={setPropertyInsuranceYearly}
                          formatValue={formatPLN}
                          tooltip={t('extraCosts.insuranceTooltip')}
                          icon={<ReceiptIcon />}
                          {...LIMITS.propertyInsuranceYearly}
                        />
                      </div>

                      {/* Summary card */}
                      <Suspense fallback={null}>
                        <ExtraCostsSummary costs={extraCosts} />
                      </Suspense>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Rent vs Buy Section */}
              <div id="rent-vs-buy" className="border-t pt-6">
                <button
                  type="button"
                  onClick={() => {
                    const next = !rentEnabled
                    setRentEnabled(next)
                    if (next && monthlyRent === 0) setMonthlyRent(3000)
                  }}
                  className="w-full rounded-2xl bg-gradient-to-r from-violet-100 to-amber-100 border border-violet-200/50 p-5 flex items-center gap-4 cursor-pointer hover:from-violet-200/80 hover:to-amber-200/80 transition-colors group"
                >
                  <span className="text-2xl shrink-0">&#127968;&#8205;&#8596;&#65039;&#127970;</span>
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-sm font-bold text-violet-800">{t('rentVsBuy.title')}</p>
                    <p className="text-xs text-violet-600/70">{t('rentVsBuy.subtitle')}</p>
                  </div>
                  <div
                    className={`relative shrink-0 w-11 h-6 rounded-full transition-colors duration-200 ${rentEnabled ? 'bg-violet-500' : 'bg-gray-300'}`}
                  >
                    <div
                      className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${rentEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </div>
                </button>

                <AnimatePresence>
                  {rentEnabled && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="mt-4 p-4 rounded-xl bg-gradient-to-br from-violet-50/50 to-amber-50/30">
                        <MortgageSlider
                          label={t('rentVsBuy.monthlyRent')}
                          value={monthlyRent}
                          onChange={setMonthlyRent}
                          formatValue={formatPLN}
                          tooltip={t('rentVsBuy.tooltip')}
                          icon={<KeyIcon />}
                          {...LIMITS.monthlyRent}
                        />
                      </div>
                      {rentVsBuyResult && (
                        <Suspense fallback={null}>
                          <RentVsBuy
                            result={rentVsBuyResult}
                            monthlyRent={monthlyRent}
                            monthlyPayment={result.monthlyPayment}
                            loanYears={loanYears}
                            downPayment={result.downPayment}
                          />
                        </Suspense>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* Chart — lazy loaded (recharts is heavy) */}
            <Suspense fallback={<div className="h-80 rounded-xl bg-muted/50 animate-pulse" />}>
              <PaymentChart input={input} overpaymentComparison={overpaymentComparison} installmentType={installmentType} />
            </Suspense>

          </div>

          {/* Right column: Results (sticky on desktop) */}
          <motion.div
            className="space-y-4 lg:sticky lg:top-24 lg:self-start"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <ResultsSummary result={result} />
            <AffordabilityGauge
              monthlyPayment={result.monthlyPayment}
              monthlyIncome={monthlyIncome}
            />
          </motion.div>
        </div>

        {/* Scenario comparison — lazy loaded, always at the bottom */}
        <div className="mt-8 lg:max-w-[calc(100%-380px-2rem)]">
          <Suspense fallback={<div className="h-40 rounded-xl bg-muted/50 animate-pulse" />}>
            <ScenarioComparison
              scenarios={scenarios}
              onSave={handleSaveScenario}
              onClear={handleClearScenarios}
              canSave={scenarios.length < 3}
            />
          </Suspense>
        </div>
      </div>

      <HomepageContent faqs={HOMEPAGE_FAQS} />

      <EmbedPopup />
    </div>
  )
}
